import { MediaConstraints, RTCSession } from '@wui/jssip/lib/RTCSession'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { useCallSessionRoomStore } from '@/entities/call-session'
import { eventBus, useWebRTC } from '@/entities/webrtc'

import { parseMediaConstraintsFromSDP } from '@/shared/utils/jssip'

type StoreMapKey = string

const DIVIDER = '@'

const ofStoreMapKey = (sessionId: RTCSession['id'], participantNumber: string): StoreMapKey => {
  return `${sessionId}${DIVIDER}${participantNumber}`
}

export const useCallSessionMediaStore = defineStore('call-session-media', () => {
  const mediaStatusesBySessionIdAndParticipantNumber = ref<Map<StoreMapKey, MediaConstraints>>(new Map())
  const streamsBySessionIdAndParticipantId = ref(new Map<string, MediaStream[]>())
  const streamsBySessionId = computed<Map<string, MediaStream[]>>(() => {
    const map = new Map()

    Array.from(streamsBySessionIdAndParticipantId.value.keys()).forEach((key: string) => {
      const sessionId = key.split(DIVIDER).at(0)

      const streams = streamsBySessionIdAndParticipantId.value.get(key)
      if (!streams) return

      if (!map.has(sessionId)) {
        map.set(sessionId, [...streams])
      } else {
        map.set(sessionId, [...map.get(sessionId), ...streams])
      }
    })

    return map
  })
  const webCamLastTrackId = ref()
  const screenSharingLastTrackId = ref()
  const screenSharingLastSenderMid = ref<string>()

  const currentStatusInPrivateDialog = computed<MediaConstraints | undefined>(() => {
    const currentSession = useWebRTC().currentRTCSessionFacade.value
    const room = currentSession ? useCallSessionRoomStore().getRoom(currentSession.sessionId) : undefined
    if (room && !room.isGroup && currentSession?.sessionId && currentSession.number) {
      return mediaStatusesBySessionIdAndParticipantNumber.value.get(ofStoreMapKey(currentSession.sessionId, currentSession.number))
    }
    return undefined
  })

  const addStreams = (sessionId: RTCSession['id'], participantNumber: string, streams: MediaStream[]) => {
    const key = ofStoreMapKey(sessionId, participantNumber)
    if (!streamsBySessionIdAndParticipantId.value.has(key)) {
      streamsBySessionIdAndParticipantId.value.set(key, streams)
    } else {
      const existed = streamsBySessionIdAndParticipantId.value.get(key)
      if (existed) {
        const filtered = existed.filter(stream => !streams.find(({ id }) => stream.id === id))
        streamsBySessionIdAndParticipantId.value.set(key, [...filtered, ...streams])
      }
    }
  }

  const addStream = (sessionId: RTCSession['id'], id: string, stream: MediaStream) => {
    const key = ofStoreMapKey(sessionId, id)
    if (!streamsBySessionIdAndParticipantId.value.has(key)) {
      streamsBySessionIdAndParticipantId.value.set(key, [stream])
    } else {
      const existed = streamsBySessionIdAndParticipantId.value.get(key)
      if (existed) {
        const tracksIds = new Set(stream.getTracks().map(t => t.id))
        const filtered = existed.filter(s => {
          const existedTracksIds = new Set(s.getTracks().map(t => t.id))
          let res = true
          tracksIds.forEach(id => {
            if (existedTracksIds.has(id)) {
              res = false
            }
          })
          return res
        })

        streamsBySessionIdAndParticipantId.value.set(key, [...filtered, ...[stream]])
      }
    }
  }

  const getVideoStreamsInSession = (sessionId: RTCSession['id']) => {
    return streamsBySessionId.value.get(sessionId)?.filter((s) => s.getVideoTracks().length > 0)
  }

  const getAudioStreamsInSession = (sessionId: RTCSession['id']) => {
    return streamsBySessionId.value.get(sessionId)?.filter((s) => s.getAudioTracks().length > 0)
  }

  const getStreams = (sessionId: RTCSession['id'], id: string) => {
    return streamsBySessionIdAndParticipantId.value.get(ofStoreMapKey(sessionId, id))
  }

  const getVideoStreams = (sessionId: RTCSession['id'], id: string) => {
    return streamsBySessionIdAndParticipantId.value.get(ofStoreMapKey(sessionId, id))?.filter(stream => !!stream.getVideoTracks().length)
  }

  const removeStreamsBySessionId = (sessionId: RTCSession['id']) => {
    [...streamsBySessionIdAndParticipantId.value.keys()].forEach((key: RTCSession['id']) => {
      if (key.startsWith(sessionId)) {
        streamsBySessionIdAndParticipantId.value.delete(key)
      }
    })
  }

  const removeStreamsBySessionIdAndTrackId = (sessionId: RTCSession['id'], trackId: string) => {
    [...streamsBySessionIdAndParticipantId.value.keys()].forEach((key: RTCSession['id']) => {
      if (key.startsWith(sessionId)) {
        const streams = streamsBySessionIdAndParticipantId.value.get(key)
        if (streams) {
          streamsBySessionIdAndParticipantId.value.set(key, streams.filter(stream => !stream.getTracks()?.find(track => track.id === trackId)))
        }
      }
    })
  }

  eventBus.on('addSession', (e) => {
    if (e.originator === 'remote') {
      const sdp = e.request.body
      const sessionId = e.session.id
      const number = e.request.from.uri.user
      const constraints: MediaConstraints = parseMediaConstraintsFromSDP(sdp)
      update(sessionId, number, constraints)
    }
  })

  eventBus.on('removeSession', (sessionId: string) => {
    Array.from(mediaStatusesBySessionIdAndParticipantNumber.value.keys())
      .filter((k: StoreMapKey) => k.indexOf(sessionId) === 0)
      .forEach((k) => {
        mediaStatusesBySessionIdAndParticipantNumber.value.delete(k)
      })

    removeStreamsBySessionId(sessionId)

    webCamLastTrackId.value = undefined
    screenSharingLastTrackId.value = undefined
    screenSharingLastSenderMid.value = undefined
  })

  const findBySessionIdAndParticipant = (sessionId: RTCSession['id'], participantNumber: string): MediaConstraints | undefined => {
    return mediaStatusesBySessionIdAndParticipantNumber.value.get(ofStoreMapKey(sessionId, participantNumber))
  }

  const update = (sessionId: RTCSession['id'], participantNumber: string, constraints: MediaConstraints): void => {
    const key = ofStoreMapKey(sessionId, participantNumber)
    const existedConstraints = mediaStatusesBySessionIdAndParticipantNumber.value.get(key)
    const newConstraints = existedConstraints ? { ...existedConstraints, ...constraints } : constraints
    mediaStatusesBySessionIdAndParticipantNumber.value.set(
      key,
      newConstraints,
    )
  }

  return {
    currentStatusInPrivateDialog,
    webCamLastTrackId,
    screenSharingLastTrackId,
    screenSharingLastSenderMid,
    streamsBySessionId,
    findBySessionIdAndParticipant,
    update,
    addStreams,
    addStream,
    getStreams,
    removeStreamsBySessionId,
    getVideoStreams,
    getVideoStreamsInSession,
    getAudioStreamsInSession,
    removeStreamsBySessionIdAndTrackId,
  }
})
